import React from 'react';
import { Calendar, CalendarCheck, Trash2, FlaskConical } from 'lucide-react';
import PatientAvatar from '../../WorkListTable/components/PatientAvatar';
import { V2Button } from '../../../shared/custom-ui/v2-components';
import { User } from 'lucide-react';
import { buildPatientDisplayName } from '../../../shared/utils/nameUtils';

const Icons = { Calendar, CalendarCheck, User, FlaskConical } as any;

interface PatientsLayoutProps {
    data: any[];
    loading?: boolean;
    onRowClick?: (row: any) => void;
    onAppointment?: (row: any) => void;
    onLabOrder?: (row: any) => void;
    onDelete?: (row: any) => void;
    scale?: number;
}

const formatDate = (value?: string | number | Date) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(d);
};

const getGenderConfig = (gender: string) => {
    const g = (gender || '').toUpperCase();
    if (g === 'M' || g === 'MALE') return { label: 'Male', color: 'bg-blue-50 text-blue-700 border-blue-200', bar: 'bg-blue-500' };
    if (g === 'F' || g === 'FEMALE') return { label: 'Female', color: 'bg-pink-50 text-pink-700 border-pink-200', bar: 'bg-pink-500' };
    return { label: gender || 'Unknown', color: 'bg-gray-50 text-gray-600 border-gray-200', bar: 'bg-transparent' };
};

const PatientsLayout: React.FC<PatientsLayoutProps> = ({
    data,
    loading = false,
    onRowClick,
    onAppointment,
    onLabOrder,
    onDelete,
    scale = 1,
}) => {

    if (loading) {
        return <PatientsSkeleton />;
    }

    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-24 h-24 mb-6 rounded-full flex items-center justify-center bg-[var(--v2-bg-card-inner)]">
                    <div className="w-12 h-12 text-[var(--v2-text-muted)]">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2m22-4l-4-4m0 0l-4 4m4-4v12" />
                        </svg>
                    </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-[var(--v2-text-heading)]">No patients found</h3>
                <p className="max-w-md mx-auto text-[15px] text-[var(--v2-text-muted)]">
                    No patient records match your criteria. Try adjusting your search or register a new patient.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 pb-4">
            {/* Desktop Header */}
            <div className="hidden lg:block" style={{ zoom: scale }}>
                {/* Header Row */}
                <div className="depth-table-header grid grid-cols-[50px_minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] gap-3 px-6 py-3.5 text-[14px] font-bold uppercase tracking-wider whitespace-nowrap">
                    <div>&nbsp;</div> {/* Avatar */}
                    <div>Patient / MRN</div>
                    <div>Gender</div>
                    <div>Birth Date</div>
                    <div>Registration Date</div>
                    <div className="text-center">Actions</div>
                </div>
            </div>

            {/* Desktop rows */}
            <div className="hidden lg:flex lg:flex-col lg:gap-3" style={{ zoom: scale }}>
                {data.map((row, index) => {
                    const genderConfig = getGenderConfig(row.Sex);
                    return (
                        <div
                            key={row.PK || row.MRN || index}
                            onClick={() => onRowClick?.(row)}
                            className="group relative rounded-xl cursor-pointer overflow-hidden transition-all duration-200 border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-[var(--v2-shadow-card)] hover:shadow-[var(--v2-shadow-card-hover)] hover:border-[var(--v2-border-glass-strong)]"
                        >
                            <div className="p-4 grid grid-cols-[50px_minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] gap-3 items-center">
                                {/* Avatar */}
                                <div>
                                    <PatientAvatar name={buildPatientDisplayName(row)} mrn={row.MRN} />
                                </div>

                                {/* Patient + MRN */}
                                <div className="overflow-hidden">
                                    <h4 className="font-semibold text-[15px] truncate whitespace-nowrap text-[var(--v2-text-primary)]">
                                        {buildPatientDisplayName(row) || 'Unknown Patient'}
                                    </h4>
                                    <span className="text-[14px] whitespace-nowrap text-[var(--v2-text-muted)]">
                                        MRN: {row.MRN || 'N/A'}
                                    </span>
                                </div>

                                {/* Gender Badge */}
                                <div>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[14px] font-medium border ${genderConfig.color}`}>
                                        {genderConfig.label}
                                    </span>
                                </div>

                                {/* Birth Date */}
                                <div>
                                    <span className="text-[15px] flex items-center gap-1.5 text-[var(--v2-text-primary)]">
                                        <Icons.Calendar className="w-4 h-4 text-[var(--v2-text-muted)]" />
                                        {formatDate(row.BirthDate)}
                                    </span>
                                </div>

                                {/* Registration Date */}
                                <div>
                                    <span className="text-[15px] flex items-center gap-1.5 text-[var(--v2-text-primary)]">
                                        <Icons.Calendar className="w-4 h-4 text-[var(--v2-text-muted)]" />
                                        {formatDate(row.Date)}
                                    </span>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                    {onLabOrder && (
                                        <V2Button
                                            size="small"
                                            variant="secondary"
                                            leftIcon={<Icons.FlaskConical style={{ width: 14, height: 14 }} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onLabOrder(row);
                                            }}
                                            title="Create new lab order"
                                        >
                                            Lab
                                        </V2Button>
                                    )}
                                    {onAppointment && (
                                        <V2Button
                                            size="small"
                                            variant="secondary"
                                            leftIcon={<Icons.CalendarCheck style={{ width: 14, height: 14 }} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onAppointment(row);
                                            }}
                                        >
                                            Appt
                                        </V2Button>
                                    )}
                                    {onDelete && (
                                        <V2Button
                                            size="small"
                                            variant="ghost"
                                            leftIcon={<Trash2 style={{ width: 13, height: 13, color: 'var(--v2-danger-500, #ef4444)' }} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(row);
                                            }}
                                            title="Delete patient"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Left color bar */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${genderConfig.bar}`} />
                        </div>
                    );
                })}
            </div>

            {/* Mobile Card Layout — Slim compact cards */}
            <div className="lg:hidden flex flex-col gap-3 px-0.5">
                {data.map((row, index) => {
                    const genderConfig = getGenderConfig(row.Sex);
                    const accentColor =
                        row.Sex?.toUpperCase() === 'M' || row.Sex?.toUpperCase() === 'MALE' ? '#3b82f6' :
                        row.Sex?.toUpperCase() === 'F' || row.Sex?.toUpperCase() === 'FEMALE' ? '#ec4899' : '#818cf8';

                    return (
                        <div
                            key={row.PK || row.MRN || index}
                            onClick={() => onRowClick?.(row)}
                            className="relative rounded-xl cursor-pointer overflow-hidden transition-all duration-200 active:scale-[0.99]"
                            style={{
                                background: 'var(--v2-bg-card)',
                                border: '1px solid var(--v2-border-card)',
                                borderLeft: `3px solid ${accentColor}`,
                                boxShadow: '0 2px 8px -2px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.05)',
                            }}
                        >
                            {/* Row 1: Avatar + Name + ID */}
                            <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2.5">
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <div
                                        className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-[13px] font-black text-white"
                                        style={{ background: accentColor }}
                                    >
                                        {buildPatientDisplayName(row) ? buildPatientDisplayName(row).charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-[14px] leading-tight truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                            {buildPatientDisplayName(row) || 'Unknown Patient'}
                                        </p>
                                        <p className="text-[11px] font-medium" style={{ color: 'var(--v2-text-muted)' }}>
                                            MRN: {row.MRN || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Row 2: Pills inline */}
                            <div
                                className="flex items-center gap-2 px-4 py-2 flex-wrap"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)' }}
                            >
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${genderConfig.color}`}>
                                    {genderConfig.label}
                                </span>
                                <span className="text-[var(--v2-border-card)] select-none">·</span>
                                <div className="flex items-center gap-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--v2-text-muted)' }}>DOB:</span>
                                    <span className="text-[11px] font-medium" style={{ color: 'var(--v2-text-primary)' }}>{formatDate(row.BirthDate)}</span>
                                </div>
                            </div>

                            {/* Row 3: Meta */}
                            <div
                                className="flex items-center gap-2 px-4 py-2.5"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)', background: 'var(--v2-bg-card-inner)' }}
                            >
                                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--v2-text-muted)' }}>Reg:</span>
                                <span className="text-[11px] font-medium" style={{ color: 'var(--v2-text-primary)' }}>
                                    {formatDate(row.Date)}
                                </span>
                            </div>

                            {/* Row 4: Actions */}
                            <div
                                className="flex items-center justify-end gap-1.5 px-4 py-2"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)' }}
                            >
                                {onAppointment && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onAppointment(row); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg transition-all duration-150 active:scale-95"
                                        style={{
                                            color: 'var(--v2-text-secondary)',
                                            background: 'var(--v2-bg-card)',
                                            border: '1px solid var(--v2-border-subtle)',
                                        }}
                                    >
                                        <Icons.CalendarCheck className="w-3.5 h-3.5" />
                                        Appt
                                    </button>
                                )}
                                {onDelete && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDelete(row); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg transition-all duration-150 active:scale-95"
                                        style={{
                                            color: '#ef4444',
                                            background: 'rgba(239,68,68,0.07)',
                                            border: '1px solid rgba(239,68,68,0.15)',
                                        }}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ─── Skeleton ─── */
const PatientsSkeleton = () => (
    <div className="flex flex-col gap-3 pb-4">
        {/* Header */}
        <div className="hidden lg:block rounded-xl overflow-hidden mb-1" style={{ border: '1px solid var(--v2-border-card)', background: 'var(--v2-table-header-bg)' }}>
            <div className="depth-table-header p-3 grid grid-cols-[50px_minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] gap-3 items-center">
                <div />
                <div>PATIENT / MRN</div>
                <div>GENDER</div>
                <div>BIRTH DATE</div>
                <div>REGISTRATION DATE</div>
                <div className="text-center">ACTIONS</div>
            </div>
        </div>

        {/* Rows */}
        {[...Array(6)].map((_, i) => (
            <div
                key={i}
                className="rounded-xl border border-l-4 border-l-transparent shadow-sm overflow-hidden"
                style={{ minHeight: '80px', background: 'var(--v2-bg-card)', borderColor: 'var(--v2-border-subtle)' }}
            >
                <div className="p-4 hidden lg:grid lg:grid-cols-[50px_minmax(0,1.8fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] lg:gap-3 lg:items-center">
                    {/* Avatar */}
                    <div className="relative overflow-hidden rounded-full w-10 h-10" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Patient */}
                    <div className="space-y-2">
                        <div className="relative overflow-hidden rounded h-4 w-3/4" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                        <div className="relative overflow-hidden rounded h-3 w-1/2" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                    </div>
                    {/* Gender */}
                    <div className="relative overflow-hidden rounded-full h-6 w-16" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Birth Date */}
                    <div className="relative overflow-hidden rounded h-3 w-20" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Registration Date */}
                    <div className="relative overflow-hidden rounded h-3 w-24" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Actions */}
                    <div className="flex items-center justify-center">
                        <div className="relative overflow-hidden rounded-lg w-16 h-8" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                    </div>
                </div>

                {/* Mobile skeleton */}
                <div className="p-4 lg:hidden space-y-3">
                    <div className="flex items-center gap-3">
                        <div className="relative overflow-hidden rounded-full w-10 h-10" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                        <div className="flex-1 space-y-2">
                            <div className="relative overflow-hidden rounded h-4 w-3/4" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                                <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                            </div>
                            <div className="relative overflow-hidden rounded h-3 w-1/2" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                                <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="relative overflow-hidden rounded h-6 w-20" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export default PatientsLayout;
